import { useCallback, useEffect } from 'react';
import { Resetter, atom, useRecoilState, useResetRecoilState } from 'recoil';
import { LOCAL_STORAGE_KEY } from 'state/localStorage';

export type OrganizationState = {
  clientToken: string;
  companyName: string;
  email: string;
  firstName: string;
  lastName: string;
  poNumber?: string;
  address?: string;
  postalCode?: string;
  verificationToken?: string;
  userId?: number;
  proSeats: number;
  regularSeats: number;
  transactionAmount?: number;
};

type UseOrganizationStateProps = {
  stateUpdateData?: Partial<OrganizationState>;
};

type UseOrganizationState = OrganizationState & {
  updateOrganizationState: (newState: Partial<OrganizationState>) => void;
  resetOrganizationState: Resetter;
};

const DEFAULT_ORGANIZATION_STATE: OrganizationState = {
  clientToken: '',
  companyName: '',
  email: '',
  firstName: '',
  lastName: '',
  proSeats: 0,
  regularSeats: 0,
};

const organizationStateAtom = atom({
  key: LOCAL_STORAGE_KEY.ORGANIZATION_STATE,
  default: DEFAULT_ORGANIZATION_STATE,
});

export const useOrganizationState = (
  props: UseOrganizationStateProps = {},
): UseOrganizationState => {
  const { stateUpdateData } = props;
  const resetOrganizationState = useResetRecoilState(organizationStateAtom);
  const [organizationState, setOrganizationState] = useRecoilState(
    organizationStateAtom,
  );

  const updateOrganizationState = useCallback(
    (newState: Partial<OrganizationState>) => {
      setOrganizationState((prev) => ({
        ...prev,
        ...newState,
      }));
    },
    [setOrganizationState],
  );

  useEffect(() => {
    if (stateUpdateData) {
      updateOrganizationState(stateUpdateData);
    }
  }, [stateUpdateData, updateOrganizationState]);

  return {
    ...organizationState,
    updateOrganizationState,
    resetOrganizationState,
  };
};
